import React, { useState, useEffect, useRef, useCallback } from 'react';

const CreateNotificationModal = ({ isOpen, onClose, onCreated, user, showToast }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('ALL'); // 'ALL' | 'CLASS'
  const [courseScheduleId, setCourseScheduleId] = useState('');
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classSearch, setClassSearch] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const modalRef = useRef(null);

  const handleCancel = useCallback(() => {
    setTitle('');
    setDescription('');
    setVisibility('ALL');
    setCourseScheduleId('');
    setClassSearch('');
    setFieldErrors({});
    onClose();
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) {
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    if (modalRef.current) modalRef.current.focus();
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, submitting, handleCancel]);

  // Fetch upcoming classes when modal opens or when CLASS is selected
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    const fetchClasses = async () => {
      setLoadingClasses(true);
      try {
        const res = await fetch('/api/admin/notification-classes', {
          headers: {
            'x-user-role': user?.role || 'Admin',
            'x-admin-role': user?.role || 'Admin',
            'x-user-id': user?.id || user?.adminId || ''
          }
        });
        const data = await res.json();
        if (isMounted && res.ok && data.status === 'success') {
          setClasses(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load upcoming classes:', err);
      } finally {
        if (isMounted) setLoadingClasses(false);
      }
    };

    fetchClasses();
    return () => { isMounted = false; };
  }, [isOpen, user]);

  const validate = () => {
    const errors = {};
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      errors.title = 'Title is required.';
    } else if (trimmedTitle.length < 3 || trimmedTitle.length > 150) {
      errors.title = 'Title must be between 3 and 150 characters.';
    }

    const trimmedDesc = description.trim();
    if (!trimmedDesc) {
      errors.description = 'Description is required.';
    } else if (trimmedDesc.length < 1 || trimmedDesc.length > 2000) {
      errors.description = 'Description must not exceed 2000 characters.';
    }

    if (visibility === 'CLASS' && !courseScheduleId) {
      errors.courseScheduleId = 'A specific class must be selected.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        visibility,
        courseScheduleId: visibility === 'CLASS' ? parseInt(courseScheduleId, 10) : null
      };

      const res = await fetch('/api/admin/notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': user?.role || 'Admin',
          'x-admin-role': user?.role || 'Admin',
          'x-user-id': user?.id || user?.adminId || ''
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        showToast('success', 'Notification published');
        handleCancel();
        if (onCreated) onCreated(data.data);
      } else {
        if (data.fieldErrors) {
          setFieldErrors(data.fieldErrors);
        }
        showToast('error', data.message || 'Failed to publish notification. Please try again.');
      }
    } catch (err) {
      console.error('Network error publishing notification:', err);
      showToast('error', 'Failed to publish notification. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Filter classes by search input
  const filteredClasses = classes.filter(c =>
    c.label.toLowerCase().includes(classSearch.toLowerCase())
  );

  const isFormValid =
    title.trim().length >= 3 &&
    title.trim().length <= 150 &&
    description.trim().length >= 1 &&
    description.trim().length <= 2000 &&
    (visibility === 'ALL' || Boolean(courseScheduleId));

  return (
    <div
      className="nc-modal-overlay"
      onClick={handleCancel}
      data-testid="create-notification-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nc-create-title"
    >
      <div
        className="nc-modal-container"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        tabIndex={-1}
      >
        <div className="nc-modal-header">
          <h3 id="nc-create-title">Create Notification</h3>
          <button
            type="button"
            className="nc-modal-close-btn"
            onClick={handleCancel}
            disabled={submitting}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="nc-modal-body">
            {/* Title field */}
            <div className="nc-form-group">
              <label htmlFor="nc-input-title" className="nc-form-label">
                <span>Title <span style={{ color: '#ef4444' }}>*</span></span>
                <span className="nc-char-counter">{title.length}/150</span>
              </label>
              <input
                id="nc-input-title"
                data-testid="nc-input-title"
                type="text"
                className={`nc-input-text ${fieldErrors.title ? 'nc-input-error' : ''}`}
                placeholder="e.g. Schedule Maintenance, Room Change"
                value={title}
                maxLength={150}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (fieldErrors.title) {
                    setFieldErrors(prev => ({ ...prev, title: null }));
                  }
                }}
                disabled={submitting}
              />
              {fieldErrors.title && (
                <span className="nc-error-text" data-testid="error-title">{fieldErrors.title}</span>
              )}
            </div>

            {/* Description field */}
            <div className="nc-form-group">
              <label htmlFor="nc-input-desc" className="nc-form-label">
                <span>Description <span style={{ color: '#ef4444' }}>*</span></span>
                <span className="nc-char-counter">{description.length}/2000</span>
              </label>
              <textarea
                id="nc-input-desc"
                data-testid="nc-input-desc"
                className={`nc-textarea ${fieldErrors.description ? 'nc-input-error' : ''}`}
                rows={5}
                placeholder="Enter notification details. Line breaks will be preserved."
                value={description}
                maxLength={2000}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (fieldErrors.description) {
                    setFieldErrors(prev => ({ ...prev, description: null }));
                  }
                }}
                disabled={submitting}
              />
              {fieldErrors.description && (
                <span className="nc-error-text" data-testid="error-desc">{fieldErrors.description}</span>
              )}
            </div>

            {/* Visibility Radio Group */}
            <div className="nc-form-group">
              <span className="nc-form-label">
                Visibility <span style={{ color: '#ef4444' }}>*</span>
              </span>
              <div className="nc-radio-group">
                <label className="nc-radio-label">
                  <input
                    type="radio"
                    name="nc-visibility"
                    value="ALL"
                    checked={visibility === 'ALL'}
                    onChange={() => {
                      setVisibility('ALL');
                      setCourseScheduleId('');
                      setFieldErrors(prev => ({ ...prev, courseScheduleId: null }));
                    }}
                    disabled={submitting}
                    data-testid="radio-visibility-all"
                  />
                  <span>All learners</span>
                </label>
                <label className="nc-radio-label">
                  <input
                    type="radio"
                    name="nc-visibility"
                    value="CLASS"
                    checked={visibility === 'CLASS'}
                    onChange={() => setVisibility('CLASS')}
                    disabled={submitting}
                    data-testid="radio-visibility-class"
                  />
                  <span>Specific class</span>
                </label>
              </div>
            </div>

            {/* Class Dropdown (only when Specific class selected) */}
            {visibility === 'CLASS' && (
              <div className="nc-form-group" data-testid="class-select-section">
                <label htmlFor="nc-select-class" className="nc-form-label">
                  Class <span style={{ color: '#ef4444' }}>*</span>
                </label>

                {/* Filter input */}
                {classes.length > 5 && (
                  <input
                    type="text"
                    className="nc-input-text"
                    style={{ marginBottom: '0.4rem', fontSize: '0.85rem', padding: '0.45rem 0.75rem' }}
                    placeholder="Search classes..."
                    value={classSearch}
                    onChange={(e) => setClassSearch(e.target.value)}
                    disabled={submitting}
                  />
                )}

                <select
                  id="nc-select-class"
                  data-testid="nc-select-class"
                  className={`nc-select ${fieldErrors.courseScheduleId ? 'nc-input-error' : ''}`}
                  value={courseScheduleId}
                  onChange={(e) => {
                    setCourseScheduleId(e.target.value);
                    if (fieldErrors.courseScheduleId) {
                      setFieldErrors(prev => ({ ...prev, courseScheduleId: null }));
                    }
                  }}
                  disabled={submitting || loadingClasses}
                >
                  <option value="">
                    {loadingClasses ? 'Loading classes...' : '-- Select Upcoming / Ongoing Class --'}
                  </option>
                  {filteredClasses.map((c) => (
                    <option key={c.courseScheduleId} value={c.courseScheduleId}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.courseScheduleId && (
                  <span className="nc-error-text" data-testid="error-class">{fieldErrors.courseScheduleId}</span>
                )}
              </div>
            )}
          </div>

          <div className="nc-modal-footer">
            <button
              type="button"
              className="nc-btn-secondary"
              onClick={handleCancel}
              disabled={submitting}
              data-testid="btn-cancel-create"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="nc-btn-primary"
              disabled={submitting || !isFormValid}
              data-testid="btn-publish-notification"
            >
              {submitting ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateNotificationModal;

